import React from "react";
import PropTypes from "prop-types";

// A stepped rating slider (§6.2 seriousness, and the continue-branch effort questions): the
// player drags along a line instead of picking one of five buttons, while the value stays an
// integer on a labelled 1..N scale - the numbers are printed under the track and the chosen
// one is read out beside it, so the rating is still a number, not a vibe.
//
// A range input always has *some* value, so "not answered yet" is modelled explicitly: while
// `value` is null the thumb parks at the midpoint in a dimmed, hollow state and the caller
// keeps its submit disabled. Any interaction commits - a change event, or a pointer release
// on the thumb without moving it (the player tapping the midpoint means the midpoint).
const range = (min, max) => Array.from({ length: max - min + 1 }, (_, i) => min + i);

const TopikScaleSlider = ({
  value,
  min,
  max,
  onChange,
  lowLabel,
  highLabel,
  ariaLabel,
  readout,
  unsetLabel,
  disabled,
}) => {
  const unset = value === null || value === undefined;
  const midpoint = Math.round((min + max) / 2);
  const shown = unset ? midpoint : value;
  const percent = ((shown - min) / (max - min)) * 100;

  const commit = (raw) => {
    if (disabled) return;
    const next = Number(raw);
    if (Number.isNaN(next)) return;
    onChange(next);
  };

  const tickClass = (n) => {
    const filled = !unset && n <= value ? " topik-slider__tick--filled" : "";
    const current = !unset && n === value ? " topik-slider__tick--current" : "";
    return `topik-slider__tick${filled}${current}`;
  };

  return (
    <div className={`topik-slider${unset ? " topik-slider--unset" : ""}${disabled ? " topik-slider--disabled" : ""}`}>
      <div className="topik-slider__legend" aria-hidden="true">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
      <div className="topik-slider__track-wrap">
        <input
          type="range"
          className="topik-slider__input"
          min={min}
          max={max}
          step={1}
          value={shown}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-valuetext={unset ? unsetLabel : readout(value)}
          onChange={(e) => commit(e.target.value)}
          // A tap that lands on the value already shown fires no change event; the release
          // still counts as choosing that value.
          onPointerUp={(e) => commit(e.target.value)}
          onKeyUp={(e) => commit(e.target.value)}
          style={{ "--topik-slider-percent": `${percent}%` }}
        />
        <div className="topik-slider__ticks" aria-hidden="true">
          {range(min, max).map((n) => (
            <span key={n} className={tickClass(n)}>{n}</span>
          ))}
        </div>
      </div>
      <output className="topik-slider__readout" aria-live="polite">
        {unset ? unsetLabel : readout(value)}
      </output>
    </div>
  );
};

TopikScaleSlider.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  lowLabel: PropTypes.string.isRequired,
  highLabel: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  // Formats a committed value for the readout + aria-valuetext, e.g. "3 / 5".
  readout: PropTypes.func.isRequired,
  unsetLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

TopikScaleSlider.defaultProps = {
  value: null,
  min: 1,
  max: 5,
  disabled: false,
};

export default TopikScaleSlider;
